// TenantBrandingPage.jsx — per-domain branding editor for the tenant portal.
//
// Same field set for every domain (TotalFusion, IPVS/Yabbit, etc.) — it's
// the template that keeps branding capabilities uniform across tenants:
//   • Accent colour (hex)
//   • Wordmark logo (upload or URL)
//   • Square icon (upload or URL)
//   • Company name (drives the sidebar wordmark caption, training area
//     titles, and any place we say "<Company> VoiceFabric …")
//   • Display font family + optional custom font file (.woff2)
//   • Body font family + optional custom font file (.woff2)
//
// Access:
//   • view: domain_admin, domain_supervisor, master_admin
//   • edit: domain_admin, master_admin only (supervisors see fields
//     but the Save/Upload buttons are hidden)
//
// The Save button PATCHes /api/tenant/branding — writes are scoped to
// the caller's own domain on the backend, no cross-tenant leak risk.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Palette, Loader2, Save, Upload, X, ShieldAlert, Type, Image as ImageIcon, Building2, RefreshCw, Sparkles, Globe,
} from 'lucide-react';
import Layout from '../../components/custom/Layout';
import { useAuth } from '../../contexts/AuthContext';
import {
  passesAccentContrast, bestTextOn, rgba, darkenForText, hexToRgb,
  evaluateAccentPair,
} from '../../lib/tenantTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

const resolveAssetUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_ORIGIN}${u}`;
};

// Contrast guard — matches backend `_validate_pair` exactly. HARD BLOCK:
// fails must not save. Empty accent = pass (means "platform defaults").
// Uses the tenant's stored `accent_text_color` (default white) as the
// paired text colour for the 3:1 UI-contrast check.
const contrastReason = (accentHex, accentTextHex) => {
  const res = evaluateAccentPair(accentHex, accentTextHex);
  if (res.ok) return null;
  return { severity: res.severity || 'error', msg: res.msg };
};

/* ── AssetPicker (image OR font) ───────────────────────────────── */
const AssetPicker = ({ label, hint, kind, accept, value, onChange, token, testId, canEdit, uploadQs = '' }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const isFont = kind.startsWith('font-');

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large — 2MB max.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const sep = uploadQs ? '&' : '?';
      const r = await axios.post(
        `${API}/tenant/uploads/branding${sep}kind=${encodeURIComponent(kind)}${uploadQs ? `&${uploadQs.slice(1)}` : ''}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      onChange(r.data?.url || '');
      toast.success('Uploaded — remember to save.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const clear = () => onChange('');
  const preview = resolveAssetUrl(value);

  return (
    <div>
      <span className="text-slate-700 text-xs font-medium">{label}</span>
      <div className="mt-1 flex items-start gap-2">
        <div className="w-14 h-14 shrink-0 rounded bg-white border border-slate-200 flex items-center justify-center overflow-hidden" data-testid={`${testId}-preview`}>
          {preview ? (
            isFont ? (
              <span className="text-[10px] text-slate-500 font-semibold px-1 text-center">FONT<br />file</span>
            ) : (
              <img src={preview} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
            )
          ) : (
            <span className="text-[9px] text-slate-400 uppercase tracking-widest">Empty</span>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={!canEdit}
            placeholder="https://…  or upload below"
            className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
            data-testid={`${testId}-url`}
          />
          {canEdit && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 text-xs bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white rounded px-2.5 py-1 font-semibold"
                data-testid={`${testId}-upload-btn`}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
              </button>
              {value && (
                <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600" data-testid={`${testId}-clear-btn`}>
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handlePick}
                className="hidden"
              />
            </div>
          )}
          {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

/* ── Page ──────────────────────────────────────────────────────── */
/**
 * @param {object} props
 * @param {string} [props.domainIdOverride]  Master-admin override — pass to
 *   view/edit another tenant's branding. Automatically appended to every
 *   backend call as `?domain_id=<override>`. When omitted, endpoints
 *   scope to the caller's own domain.
 * @param {boolean} [props.embedded]  When true, skip the `<Layout>` wrapper
 *   so this page can render inside another admin layout (used by
 *   `AdminBrandingPage`).
 */
const TenantBrandingPage = ({ domainIdOverride = '', embedded = false }) => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const canEdit = user?.role === 'master_admin' || user?.role === 'domain_admin';
  const qs = domainIdOverride ? `?domain_id=${encodeURIComponent(domainIdOverride)}` : '';

  const [form, setForm] = useState({
    accent_color: '', accent_text_color: '', logo_url: '', icon_url: '', company_name: '',
    font_family_display: '', font_family_body: '',
    font_url_display: '', font_url_body: '',
    banner_url: '', banner_color: '',
  });
  const [original, setOriginal] = useState(form);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/tenant/branding${qs}`, { headers });
      const shape = {
        accent_color: r.data.accent_color || '',
        accent_text_color: r.data.accent_text_color || '',
        logo_url: r.data.logo_url || '',
        icon_url: r.data.icon_url || '',
        company_name: r.data.company_name || '',
        font_family_display: r.data.font_family_display || '',
        font_family_body: r.data.font_family_body || '',
        font_url_display: r.data.font_url_display || '',
        font_url_body: r.data.font_url_body || '',
        banner_url: r.data.banner_url || '',
        banner_color: r.data.banner_color || '',
      };
      setForm(shape); setOriginal(shape);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load branding.');
    } finally { setLoading(false); }
  }, [headers, qs]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => Object.keys(form).some((k) => (form[k] || '').trim() !== (original[k] || '').trim()),
    [form, original],
  );

  const colorErr = useMemo(
    () => contrastReason(form.accent_color.trim(), form.accent_text_color.trim()),
    [form.accent_color, form.accent_text_color],
  );
  const colorErrBlocks = !!colorErr; // Every reason is now a HARD BLOCK (spec item 14)

  const save = useCallback(async () => {
    if (colorErrBlocks) { toast.error(colorErr.msg); return; }
    setSaving(true);
    try {
      const changed = Object.keys(form).reduce((acc, k) => {
        if ((form[k] || '').trim() !== (original[k] || '').trim()) acc[k] = form[k].trim();
        return acc;
      }, {});
      await axios.patch(`${API}/tenant/branding${qs}`, changed, { headers });
      toast.success('Branding saved — refresh the page to see the new theme applied.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }, [form, original, colorErr, colorErrBlocks, headers, load, qs]);

  // ── Brand crawler (master-admin only; the endpoint is master-only anyway)
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const runCrawl = useCallback(async () => {
    if (!crawlUrl.trim()) return;
    setCrawling(true); setCrawlResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/branding/crawl-website`,
        { url: crawlUrl.trim(), refine_with_ai: true },
        { headers },
      );
      setCrawlResult(r.data);
      if (!r.data.sniff?.ok) {
        toast.error(r.data.sniff?.problem || 'Could not read that URL.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Crawl failed');
    } finally { setCrawling(false); }
  }, [crawlUrl, headers]);
  const applySuggestion = (key, value) => {
    if (!value) return;
    setForm((f) => ({ ...f, [key]: value }));
    toast.success('Applied — remember to Save.');
  };
  const canCrawl = user?.role === 'master_admin' || user?.role === 'domain_admin';

  if (loading) {
    const body = (
      <div className="p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
    return embedded ? body : <Layout>{body}</Layout>;
  }

  const previewAccent = form.accent_color.trim() || '#0B9DB6';
  const previewBadgeBg = form.accent_color.trim() ? rgba(form.accent_color.trim(), 0.12) : 'rgb(255 251 235)';
  const previewBadgeBorder = form.accent_color.trim() ? rgba(form.accent_color.trim(), 0.35) : 'rgb(253 230 138)';
  const previewBadgeText = form.accent_color.trim() ? darkenForText(form.accent_color.trim()) : 'rgb(146 64 14)';
  // Prefer the tenant's locked accent_text if set; else auto-pick.
  const previewFillText = form.accent_text_color.trim() && hexToRgb(form.accent_text_color.trim())
    ? form.accent_text_color.trim()
    : (form.accent_color.trim() ? bestTextOn(form.accent_color.trim()) : '#ffffff');

  const content = (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="tenant-branding-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Palette className="w-6 h-6 text-emerald-500" />
              Branding
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Set how your site looks and reads &mdash; colour, logo, icon, company name, and typography.
              These fields appear on every domain so each tenant can style their own portal.
              {!canEdit && <span className="block mt-1 text-amber-700 italic">You&apos;re viewing in read-only mode. Ask a domain admin to make changes.</span>}
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving || colorErrBlocks}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-lg"
              data-testid="branding-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
          )}
        </div>

        {err && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-rose-700 text-sm">
            {err}
          </div>
        )}

        {/* Brand crawler — suggest from a URL */}
        {canCrawl && (
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4" data-testid="brand-crawler-card">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-2">
              <Sparkles className="w-4 h-4 text-teal-600" /> Suggest branding from a website
              <span className="text-[10px] uppercase tracking-widest text-teal-700 bg-teal-100 border border-teal-200 rounded px-1.5 py-0.5 ml-1">AI</span>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Paste your company URL. We&apos;ll read the site&apos;s meta tags and ask Claude to curate 1&ndash;3 candidate colours plus a logo/icon guess. Click <b>Use this</b> beside any suggestion to drop it into the form.
            </p>
            <div className="flex gap-2 flex-wrap">
              <div className="flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden flex-1 min-w-[260px]">
                <Globe className="w-4 h-4 text-slate-400 ml-3" />
                <input
                  type="url"
                  value={crawlUrl}
                  onChange={(e) => setCrawlUrl(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="flex-1 bg-transparent px-2 py-2 text-slate-900 text-sm focus:outline-none"
                  data-testid="brand-crawler-url"
                />
              </div>
              <button
                type="button"
                onClick={runCrawl}
                disabled={crawling || !crawlUrl.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white font-semibold rounded-lg"
                data-testid="brand-crawler-run"
              >
                {crawling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Suggest
              </button>
            </div>

            {crawlResult?.sniff?.ok && (
              <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="brand-crawler-result">
                {crawlResult.refined?.tone && (
                  <p className="text-[11px] text-slate-500 italic">&ldquo;{crawlResult.refined.tone}&rdquo;</p>
                )}
                {/* Company name */}
                {(crawlResult.refined?.company_name_suggestion || crawlResult.sniff.company_name) && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-20">Name:</span>
                    <b className="text-slate-900">{crawlResult.refined?.company_name_suggestion || crawlResult.sniff.company_name}</b>
                    <button type="button" onClick={() => applySuggestion('company_name', crawlResult.refined?.company_name_suggestion || crawlResult.sniff.company_name)} className="ml-auto text-[10px] font-semibold uppercase bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded px-2 py-0.5">Use this</button>
                  </div>
                )}
                {/* Refined colours (AI curated) */}
                {(crawlResult.refined?.refined_colors || []).map((c) => (
                  <div key={c.hex} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-20">Colour:</span>
                    <span className="w-4 h-4 rounded border border-slate-300 shrink-0" style={{ backgroundColor: c.hex }} />
                    <span className="font-mono text-slate-900">{c.hex}</span>
                    <span className="text-slate-500">— {c.role}</span>
                    <button type="button" onClick={() => applySuggestion('accent_color', c.hex)} className="ml-auto text-[10px] font-semibold uppercase bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded px-2 py-0.5">Use this</button>
                  </div>
                ))}
                {/* Raw sniffed colours as fallback */}
                {(!crawlResult.refined?.refined_colors || crawlResult.refined.refined_colors.length === 0) && (crawlResult.sniff.candidate_colors || []).slice(0, 3).map((c) => (
                  <div key={c.hex} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-20">Colour:</span>
                    <span className="w-4 h-4 rounded border border-slate-300 shrink-0" style={{ backgroundColor: c.hex }} />
                    <span className="font-mono text-slate-900">{c.hex}</span>
                    <span className="text-slate-500">— from {c.source}</span>
                    <button type="button" onClick={() => applySuggestion('accent_color', c.hex)} className="ml-auto text-[10px] font-semibold uppercase bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded px-2 py-0.5">Use this</button>
                  </div>
                ))}
                {/* Logo + icon */}
                {crawlResult.sniff.candidate_logo && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-20">Logo:</span>
                    <a href={crawlResult.sniff.candidate_logo} target="_blank" rel="noreferrer" className="text-teal-700 underline truncate max-w-[280px]">{crawlResult.sniff.candidate_logo}</a>
                    <button type="button" onClick={() => applySuggestion('logo_url', crawlResult.sniff.candidate_logo)} className="ml-auto text-[10px] font-semibold uppercase bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded px-2 py-0.5">Use this</button>
                  </div>
                )}
                {crawlResult.sniff.candidate_icon && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-20">Icon:</span>
                    <a href={crawlResult.sniff.candidate_icon} target="_blank" rel="noreferrer" className="text-teal-700 underline truncate max-w-[280px]">{crawlResult.sniff.candidate_icon}</a>
                    <button type="button" onClick={() => applySuggestion('icon_url', crawlResult.sniff.candidate_icon)} className="ml-auto text-[10px] font-semibold uppercase bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded px-2 py-0.5">Use this</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* ── Editors ── */}
          <div className="lg:col-span-3 space-y-4">
            {/* Identity */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm" data-testid="branding-identity-card">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <Building2 className="w-4 h-4 text-emerald-500" /> Identity
              </div>
              <label className="block text-sm">
                <span className="text-slate-700 text-xs font-medium">Company name</span>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  disabled={!canEdit}
                  placeholder="e.g. TotalFusion"
                  className="mt-1 w-full bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                  data-testid="branding-company-name"
                />
                <p className="mt-1 text-[10px] text-slate-500">
                  Shown in the sidebar wordmark caption, training area titles, and export footers.
                </p>
              </label>
            </div>

            {/* Colour */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm" data-testid="branding-color-card">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <Palette className="w-4 h-4 text-emerald-500" /> Accent colour
              </div>
              <label className="block text-sm">
                <span className="text-slate-700 text-xs font-medium">Hex value</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={form.accent_color || '#0B9DB6'}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    disabled={!canEdit}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border border-slate-200 shrink-0 disabled:opacity-50"
                    data-testid="branding-color-picker"
                  />
                  <input
                    type="text"
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    disabled={!canEdit}
                    placeholder="#CDA177 — leave blank for the platform default"
                    maxLength={9}
                    className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 font-mono text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                    data-testid="branding-color-hex"
                  />
                </div>
                {colorErr && (
                  <p
                    className={`mt-1.5 text-xs flex items-start gap-1.5 ${colorErr.severity === 'error' ? 'text-rose-600' : 'text-amber-700'}`}
                    data-testid="branding-color-error"
                  >
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {colorErr.msg}
                  </p>
                )}
              </label>

              {/* Accent text colour — the char colour used ON TOP of the
                  accent fill (buttons, active nav, banner text). Blank
                  = auto-pick white/black by best contrast. Set explicitly
                  (e.g. #000000 for warm accents like TotalFusion's gold)
                  when you want to lock a brand-official pair. */}
              <label className="block text-sm">
                <span className="text-slate-700 text-xs font-medium">Text on the accent fill</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={form.accent_text_color || '#ffffff'}
                    onChange={(e) => setForm({ ...form, accent_text_color: e.target.value })}
                    disabled={!canEdit}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border border-slate-200 shrink-0 disabled:opacity-50"
                    data-testid="branding-accent-text-picker"
                  />
                  <input
                    type="text"
                    value={form.accent_text_color}
                    onChange={(e) => setForm({ ...form, accent_text_color: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Leave blank for auto (white on dark, black on light)"
                    maxLength={9}
                    className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 font-mono text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                    data-testid="branding-accent-text-hex"
                  />
                </div>
                <p className="mt-1 text-[10px] text-slate-500">
                  Used for button labels, active nav, and banner text. Blank auto-picks the higher-contrast of white/black. Set <code className="bg-slate-100 px-1 py-0.5 rounded">#000000</code> for warm tan/gold accents to hit WCAG AA.
                </p>
              </label>
            </div>

            {/* Logos */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm" data-testid="branding-logos-card">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <ImageIcon className="w-4 h-4 text-emerald-500" /> Logos
              </div>
              <AssetPicker
                label="Wordmark logo"
                hint="Wide horizontal lockup for the sidebar. PNG, JPG, WEBP, SVG. Falls back to the platform logo when blank."
                kind="logo"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                value={form.logo_url}
                onChange={(v) => setForm({ ...form, logo_url: v })}
                token={token}
                testId="branding-logo"
                canEdit={canEdit}
                uploadQs={qs}
              />
              <AssetPicker
                label="Square icon"
                hint="Small square icon shown left of the wordmark. Optional."
                kind="icon"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                value={form.icon_url}
                onChange={(v) => setForm({ ...form, icon_url: v })}
                token={token}
                testId="branding-icon"
                canEdit={canEdit}
                uploadQs={qs}
              />
            </div>

            {/* Typography */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm" data-testid="branding-typography-card">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <Type className="w-4 h-4 text-emerald-500" /> Typography
              </div>
              <p className="text-[11px] text-slate-500 -mt-1">
                Type a CSS font stack (e.g. <code className="bg-slate-100 px-1 py-0.5 rounded">&quot;AW Conqueror Sans&quot;, Manrope, sans-serif</code>).
                If it&apos;s not a Google/system font, also upload the .woff2 file so the browser can find it.
              </p>

              <label className="block text-sm">
                <span className="text-slate-700 text-xs font-medium">Display font (headings)</span>
                <input
                  type="text"
                  value={form.font_family_display}
                  onChange={(e) => setForm({ ...form, font_family_display: e.target.value })}
                  disabled={!canEdit}
                  placeholder='"AW Conqueror Sans", Manrope, sans-serif'
                  className="mt-1 w-full bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 font-mono text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                  data-testid="branding-font-display-family"
                />
              </label>
              <AssetPicker
                label="Display font file (optional)"
                hint="Upload the licensed .woff2 file. Only needed if the family above isn't a system or Google font."
                kind="font-display"
                accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff"
                value={form.font_url_display}
                onChange={(v) => setForm({ ...form, font_url_display: v })}
                token={token}
                testId="branding-font-display-file"
                canEdit={canEdit}
                uploadQs={qs}
              />

              <label className="block text-sm mt-2">
                <span className="text-slate-700 text-xs font-medium">Body font (paragraphs)</span>
                <input
                  type="text"
                  value={form.font_family_body}
                  onChange={(e) => setForm({ ...form, font_family_body: e.target.value })}
                  disabled={!canEdit}
                  placeholder='"Circular", Manrope, sans-serif'
                  className="mt-1 w-full bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 font-mono text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                  data-testid="branding-font-body-family"
                />
              </label>
              <AssetPicker
                label="Body font file (optional)"
                hint="Same rules as the display font — .woff2 only needed for licensed / non-system faces."
                kind="font-body"
                accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff"
                value={form.font_url_body}
                onChange={(v) => setForm({ ...form, font_url_body: v })}
                token={token}
                testId="branding-font-body-file"
                canEdit={canEdit}
                uploadQs={qs}
              />
            </div>

            {/* Banner */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm" data-testid="branding-banner-card">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <ImageIcon className="w-4 h-4 text-emerald-500" /> Banner
              </div>
              <p className="text-[11px] text-slate-500 -mt-1">
                Hero image or solid colour block that appears on the tenant&apos;s portal dashboard.
                If both are set, the image wins.
              </p>
              <AssetPicker
                label="Banner image"
                hint="Wide 3:1 or 4:1 image. Renders as the tenant's dashboard hero."
                kind="banner"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                value={form.banner_url}
                onChange={(v) => setForm({ ...form, banner_url: v })}
                token={token}
                testId="branding-banner-file"
                canEdit={canEdit}
                uploadQs={qs}
              />
              <label className="block text-sm">
                <span className="text-slate-700 text-xs font-medium">Or a solid banner colour (fallback)</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={form.banner_color || '#0B9DB6'}
                    onChange={(e) => setForm({ ...form, banner_color: e.target.value })}
                    disabled={!canEdit}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border border-slate-200 shrink-0 disabled:opacity-50"
                    data-testid="branding-banner-color-picker"
                  />
                  <input
                    type="text"
                    value={form.banner_color}
                    onChange={(e) => setForm({ ...form, banner_color: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Leave blank to fall back to the accent"
                    maxLength={9}
                    className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 text-slate-900 font-mono text-xs focus:outline-none focus:border-emerald-500 disabled:bg-slate-50"
                    data-testid="branding-banner-color-hex"
                  />
                </div>
              </label>
            </div>
          </div>

          {/* ── Live preview ── */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4 shadow-sm sticky top-4" data-testid="branding-preview">
              <div className="flex items-center justify-between">
                <div className="text-slate-900 font-semibold text-sm">Preview</div>
                <button type="button" onClick={() => load()} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />Reset
                </button>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Banner</p>
                <div
                  className="h-20 rounded flex items-center justify-center text-white text-sm font-semibold shadow-sm overflow-hidden"
                  style={{
                    backgroundColor: form.banner_color || previewAccent,
                    backgroundImage: form.banner_url ? `url("${resolveAssetUrl(form.banner_url)}")` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: previewFillText,
                  }}
                  data-testid="branding-banner-preview"
                >
                  {!form.banner_url && (form.company_name || 'Banner preview')}
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Sidebar lockup</p>
                <div className="rounded bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                  {form.icon_url ? <img src={resolveAssetUrl(form.icon_url)} alt="" className="h-8 w-8 object-contain" onError={(e) => { e.target.style.visibility = 'hidden'; }} /> : null}
                  <div className="min-w-0">
                    {form.logo_url
                      ? <img src={resolveAssetUrl(form.logo_url)} alt="" className="h-8 w-auto object-contain max-w-[140px]" />
                      : <div className="text-sm font-bold text-slate-900" style={{ fontFamily: form.font_family_display || undefined }}>{form.company_name || 'Company Name'}</div>}
                    <p className="text-[9px] uppercase tracking-widest text-slate-500 font-medium leading-none mt-0.5">
                      Powered by Yabbit VoiceFabric
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Active nav</p>
                <div
                  className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium"
                  style={{ backgroundColor: previewAccent, color: previewFillText, fontFamily: form.font_family_body || undefined }}
                >
                  Dashboard
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Scope badge</p>
                <span
                  className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                  style={{ backgroundColor: previewBadgeBg, borderColor: previewBadgeBorder, color: previewBadgeText }}
                >
                  <span className="text-[10px] uppercase tracking-wider">Site</span>
                  <span>Chermside</span>
                </span>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Training hero</p>
                <div className="rounded p-3 bg-slate-50" style={{ borderTop: `4px solid ${previewAccent}` }}>
                  <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: previewAccent, fontFamily: form.font_family_body || undefined }}>
                    VoiceFabric &gt; {form.company_name || 'Company Name'}
                  </p>
                  <p className="text-sm font-bold text-slate-900" style={{ fontFamily: form.font_family_display || undefined }}>
                    {form.company_name || 'Company Name'} VoiceFabric Training
                  </p>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Typography</p>
                <div className="space-y-1">
                  <div className="text-xl text-slate-900" style={{ fontFamily: form.font_family_display || 'inherit' }}>
                    Display font — {form.font_family_display ? 'custom' : 'default'}
                  </div>
                  <div className="text-sm text-slate-700" style={{ fontFamily: form.font_family_body || 'inherit' }}>
                    Body font — the quick brown fox jumps over the lazy dog. 1234567890.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
  return embedded ? content : <Layout>{content}</Layout>;
};

export default TenantBrandingPage;
